import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useGetCurrentUserCache } from '~user-profile/queries/useGetUserById'
import { useSearchMapsInCurrentUserProfileCache } from '~user-profile/queries/useSearchMaps'
import { Map } from '~user-profile/types/__generated/gql/graphql'
import { MapListWithFallback as View } from 'ui-components'

import { getCreateMapLink, getWorkspaceLink } from 'utils'
import { MAPS_LIMIT, SAMPLE_MAP_URL } from '~user-profile/constants/index'
import { useIsMyProfile } from '~user-profile/hooks/useIsMyProfile'
import { useAuthCurrentWorkspace } from 'user-auth'

export const MapList = () => {
  const router = useRouter()

  const isMyProfile = useIsMyProfile()
  const currentWorkspace = useAuthCurrentWorkspace()
  const { data: searchMapsData, isLoading } =
    useSearchMapsInCurrentUserProfileCache()
  const { data: user } = useGetCurrentUserCache()
  const workspaces = useMemo(() => {
    if (!user) return []

    return user.membershipCollection.items.map(
      (membership) => membership.workspace!
    )
  }, [user])

  const handleCreateMapClick = () => {
    if (workspaces.length <= 0) {
      router.push('/workspace/create')
      return
    }

    if (currentWorkspace) {
      router.push(
        getCreateMapLink({
          workspaceId: currentWorkspace.id,
          workspaceName: currentWorkspace!.name
        })
      )
    }
  }

  const handlePageChange = (_, newPage) => {
    router.push({ query: { ...router.query, page: newPage } })
  }

  const isSearching = router.query.query?.length || !!router.query.tags?.length
  const noResultsText = isSearching
    ? 'No maps found'
    : 'This account has no maps!'

  return (
    <View
      noResultsText={noResultsText}
      loading={isLoading}
      defaultMapUrl={SAMPLE_MAP_URL}
      canCreateMap={isMyProfile}
      maps={(searchMapsData?.items || []) as Map[]}
      getMapAvatar={(map) =>
        map.workspace
          ? {
              image: map.workspace.logo!,
              name: map.workspace.name || '', // Ensure name is always a string
              href: getWorkspaceLink({
                workspaceId: map.workspace.id,
                workspaceName: map.workspace.name
              }),
              updatedAt: map.createdAt
            }
          : undefined
      }
      pagination={{
        page: Number(router.query.page) ? Number(router.query.page) : 1,
        total: searchMapsData?.total || 0,
        limit: MAPS_LIMIT
      }}
      onPageChange={handlePageChange}
      onCreateMapClick={handleCreateMapClick}
    />
  )
}
